// no angular imports, or its spec cannot run under node and karma cannot compile it either

const CSS_AT_IMPORT_PATTERN = /@import\b/i;
// css escapes are legal in an at-keyword, so `@\69 mport` reaches the browser as @import
const CSS_ESCAPED_AT_RULE_PATTERN = /@[a-z0-9-]*\\/i;
// and legal in a function name, so `u\72 l(` reaches the browser as url(
const CSS_ESCAPED_FUNCTION_NAME_PATTERN = /\\(?:[a-f0-9]{1,6}\s?|[^\s(])[a-z0-9_-]*\(/i;
const CSS_URL_TARGET_PATTERN = /url\(\s*(['"]?)([^'")]*)\1\s*\)/gi;
const URL_SCHEME_PATTERN = /^([a-z][a-z0-9+.-]*):/i;
const ALLOWED_URL_SCHEMES = new Set(['data', 'https']);

export function findDisallowedCustomCssDirective(css: string): string | null {
  const { withoutCommentsOrInertStrings, withoutCommentsOrStrings } = splitCssOutsideStringsAndComments(css);

  if (!hasBalancedCssBraces(withoutCommentsOrStrings)) {
    return 'Unbalanced { }. The extra brace would let this CSS escape the drawer and restyle the storefront.';
  }

  if (CSS_AT_IMPORT_PATTERN.test(withoutCommentsOrStrings)) {
    return "@import is not allowed. It would pull in stylesheets we can't check.";
  }

  if (CSS_ESCAPED_AT_RULE_PATTERN.test(withoutCommentsOrStrings)) {
    return 'Escaped at-rules are not allowed. Write the rule name plainly so we can tell what it is.';
  }

  if (CSS_ESCAPED_FUNCTION_NAME_PATTERN.test(withoutCommentsOrStrings)) {
    return 'Escaped function names are not allowed. Write the function name plainly so we can tell what it is.';
  }

  CSS_URL_TARGET_PATTERN.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = CSS_URL_TARGET_PATTERN.exec(withoutCommentsOrInertStrings)) !== null) {
    const target = match[2].trim();
    if (!target) continue;
    if (isAllowedCssUrlTarget(target)) continue;

    return `url(${target}) is not allowed. Use a relative path, a data: URI, or https:.`;
  }

  return null;
}

// one pass: scanning strings and comments separately lets a quoted `/*` blank every check downstream
function splitCssOutsideStringsAndComments(css: string): {
  withoutCommentsOrInertStrings: string;
  withoutCommentsOrStrings: string;
} {
  let withoutCommentsOrInertStrings = '';
  let withoutCommentsOrStrings = '';
  let isUrlTargetPosition = false;
  let index = 0;

  while (index < css.length) {
    const character = css[index];

    if (character === '/' && css[index + 1] === '*') {
      const end = css.indexOf('*/', index + 2);
      index = end === -1 ? css.length : end + 2;
      withoutCommentsOrInertStrings += ' ';
      withoutCommentsOrStrings += ' ';
      continue;
    }

    if (character === '"' || character === "'") {
      const literalStart = index;
      index += 1;
      // a raw newline ends a css string, so a `}` after it is live and escapes the wrapper
      // an escaped newline is a valid continuation and is still consumed below
      while (index < css.length && css[index] !== character) {
        const stringChar = css[index];
        if (stringChar === '\n' || stringChar === '\r' || stringChar === '\f') break;
        if (stringChar === '\\') index += 1;
        index += 1;
      }
      if (css[index] === character) index += 1;
      // only a `url(` target is live css, every other string is inert text and must not be scanned
      withoutCommentsOrInertStrings += isUrlTargetPosition ? css.slice(literalStart, index) : ' ';
      withoutCommentsOrStrings += ' ';
      isUrlTargetPosition = false;
      continue;
    }

    if (character === '(') isUrlTargetPosition = /url$/i.test(withoutCommentsOrInertStrings.slice(-3));
    else if (!/\s/.test(character)) isUrlTargetPosition = false;

    withoutCommentsOrInertStrings += character;
    withoutCommentsOrStrings += character;
    index += 1;
  }

  return { withoutCommentsOrInertStrings, withoutCommentsOrStrings };
}

// an unbalanced `}` escapes the wrapper; must stay verdict-identical to the renderer's customCss.ts
function hasBalancedCssBraces(cssWithoutCommentsOrStrings: string): boolean {
  let depth = 0;
  for (const character of cssWithoutCommentsOrStrings) {
    if (character === '{') depth += 1;
    else if (character === '}') {
      depth -= 1;
      if (depth < 0) return false;
    }
  }
  return depth === 0;
}

// scheme allowlist, not same-origin: this runs on a pdq domain and the renderer on the merchant's
function isAllowedCssUrlTarget(target: string): boolean {
  // the browser unescapes `htt\70 :`, this regex does not, so the escape smuggles the scheme past
  if (target.includes('\\')) return false;
  if (target.startsWith('//')) return false;
  if (target.startsWith('#') || target.startsWith('/')) return true;

  const scheme = URL_SCHEME_PATTERN.exec(target);
  if (!scheme) return true;

  return ALLOWED_URL_SCHEMES.has(scheme[1].toLowerCase());
}
